<template>
  <section class="w-full h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat py-10"
    :style="{ backgroundImage: `url(${bg_about_business})` }">
    <div class="w-[81%] h-screen mx-auto">
      <div class="grid grid-cols-1 lg:grid-cols-10 gap-8 h-screen items-start">
        <!-- 左侧列表（保持不变） -->
        <div class="col-span-4 h-screen">
          <p class="text-left text-black font-bold text-[2.6vw] mt-3.0vw">
            {{ businessTexts.title }}
          </p>
          <div class="h-screen flex">
            <ul class="space-y-5.6vw pt-[20vh]">
              <li v-for="(segment, index) in segments" :key="segment.id"
                class="flex items-center text-lg cursor-pointer" :class="activeIndex === index
                  ? 'font-bold text-[#0d0097]'
                  : 'font-medium text-[#666666]'
                  " @click="setActiveIndex(index)">
                <span class="flex-shrink-0 h-4 w-4 rounded-full mr-4 border-2" :class="activeIndex === index
                  ? 'border-[#0d0097] bg-[#0d0097]'
                  : 'border-[#666666]'
                  "></span>
                <!-- 组件模板部分 -->
                <div class="relative inline-block text-1.4vw">
                  <span class="font-medium">{{ segment.title }}</span>
                  <img v-if="segment.tagImage" :src="segment.tagImage"
                    class="absolute bottom-full left-full transform -translate-x-[2vw] ml-[0.5vw] mb-[0.2vw] w-[6vw] rounded-md hidden"
                    alt="tag icon" />
                </div>
              </li>
            </ul>
          </div>
        </div>

        <!-- 中间图标列（使用每个 segment 的不同图标） -->
        <div class="col-span-2 h-screen flex justify-center bg-cover bg-center bg-no-repeat"
          :style="{ backgroundImage: `url(${img_about_quan})` }">
          <div class="relative flex flex-row lg:flex-col items-center justify-around h-full w-full">
            <div v-for="(segment, index) in segments" :key="segment.id" @click="setActiveIndex(index)"
              class="relative z-10 flex items-center justify-center h-6vw w-6vw rounded-full cursor-pointer transition-all duration-300 ease-in-out"
              :style="{ transform: `translateX(${segment.offsetX || '0px'})` }">
              <img :src="activeIndex === index
                ? segment.selectedIcon
                : segment.normalIcon
                " :class="[
                  'transition-transform duration-300 ease-in-out',
                  activeIndex === index
                    ? 'scale-[1.2] w-6vw h-6vw'
                    : 'w-4vw h-4vw',
                ]" alt="Segment Icon" />
            </div>
          </div>
        </div>

        <!-- 右侧内容（保持不变） -->
        <div class="col-span-4 h-screen flex justify-center items-center">
          <div class="p-4 rounded-lg">
            <Transition name="fade" mode="out-in">
              <div :key="activeIndex">
                <h3 class="text-2.1vw font-bold text-black mb-2.6vw">
                  {{ segments[activeIndex].title }}
                </h3>
                <p class="text-1.3vw text-black font-medium leading-relaxed leading-[1.7]"
                  v-html="highlightText(segments[activeIndex].content, segments[activeIndex].highlight)"></p>
              </div>
            </Transition>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'

// 背景图
import bg_about_business from "../assets/bg_about_business.png";
import img_about_quan from "../assets/img_about_quan.png";

// 图标导入（每组对应一个业务模块）
import btn_about_1_nor from "../assets/btn_about_1_nor.png";
import btn_about_1_sel from "../assets/btn_about_1_sel.png";

import btn_about_2_nor from "../assets/btn_about_2_nor.png";
import btn_about_2_sel from "../assets/btn_about_2_sel.png";

import btn_about_3_nor from "../assets/btn_about_3_nor.png";
import btn_about_3_sel from "../assets/btn_about_3_sel.png";

import btn_about_4_nor from "../assets/btn_about_4_nor.png";
import btn_about_4_sel from "../assets/btn_about_4_sel.png";

import img_about_tag from "../assets/img_about_tag.png";

// 国际化
const { locale, messages } = useI18n(); // 移除 t

// 获取业务板块文本
const businessTexts = computed(() => {
  return messages.value[locale.value]?.businessSegments || {
    title: 'Business Segments'
  };
});

// 当前选中项
const activeIndex = ref(0);

// 从语言文件获取segments数据
const segments = computed(() => {
  const segmentsData = messages.value[locale.value]?.businessSegments?.segments;
  
  // 如果是对象，转换为数组
  let segmentsArray = [];
  if (Array.isArray(segmentsData)) {
    segmentsArray = segmentsData;
  } else if (segmentsData && typeof segmentsData === 'object') {
    segmentsArray = Object.values(segmentsData);
  } else {
    return [];
  }
  
  return segmentsArray.map((segment, index) => ({
    ...segment,
    highlight: [segment.title],
    normalIcon: [btn_about_1_nor, btn_about_2_nor, btn_about_3_nor, btn_about_4_nor][index],
    selectedIcon: [btn_about_1_sel, btn_about_2_sel, btn_about_3_sel, btn_about_4_sel][index],
    tagImage: segment.hasTag ? img_about_tag : undefined
  }));
});

const highlightText = (text: string, keywords?: string[]): string => {
  if (!keywords || keywords.length === 0) return text;

  const escapedKeywords = keywords.map(k =>
    k.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  );
  const regex = new RegExp(`(${escapedKeywords.join("|")})`, "gi");

  return text.replace(
    regex,
    `<span class="text-[#0d0097] font-bold text-1.3vw">$1</span>`
  );
};

// 点击切换
const setActiveIndex = (index: number) => {
  activeIndex.value = index;
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
